// src/components/frames/ViewAsProvider.js
import Vue from 'vue'
import TicketsService from '@/services/API/TicketsService'
import { loggerMixin } from '@/helpers'
import { ROLES } from '@/domain/constants/roles'
import { Profile } from '@/domain/models/Profile'

const PROFILES_DISPLAY_TYPES = {
  CLIENT_ACCESS: 'CLIENT',
  PARTNER_ACCESS: 'DEFAULT',
  CPU_ACCESS: 'DEFAULT'
}

const store = Vue.observable({
  loading: false,
  loaded: false,
  items: [],
  selected: null,
  profiles: new Map(),
  enabled: false
})

const ViewAsProvider = {
  name: 'ViewAsProvider',
  props: {
    user: {
      type: Object,
      default: null
    },
    manageLifecycle: {
      type: Boolean,
      default: false
    }
  },
  mixins: [loggerMixin],
  data: () => ({
    _logger: null,
    _initialLoadInFlight: false
  }),
  computed: {
    selected () {
      return store.selected
    },
    items () {
      return store.items.map(el => ({
        text: ['PARTNER_ACCESS', 'CLIENT_ACCESS'].includes(el.accessType) ? el.companyName : el.name,
        value: el
      }))
    },
    profile () {
      if (!this.user) {
        return {}
      }
      if (!store.selected) {
        return this.user
      }
      const key = store.selected.value.username
      const output = store.profiles.get(key)
      return output
    },
    displayType () {
      return PROFILES_DISPLAY_TYPES[this.profile?.accessType] || 'DEFAULT'
    },
    scopes () {
      return this.profile.ticketScopes
    }
  },
  methods: {
    setItem (item) {
      this._log('setItem: ', item)
      store.selected = item
    },
    clearItem () {
      this._log('clearItem')
      store.selected = null
    },
    _hasRole () {
      if (!this.user) {
        return
      }
      return this.user.hasRole([ROLES.DEVOPS, ROLES.CPU_REP_CA])
    },
    async _loadList () {
      this._log('_loadList _initialLoadInFlight:', this._initialLoadInFlight)
      if (this._initialLoadInFlight || store.loading || store.loaded) {
        return
      }
      try {
        store.loading = true
        this._initialLoadInFlight = true
        const response = await TicketsService.getViewAsList()
        this._log('_loadList', response)
        for (const item of response) {
          const { payload, data } = item
          store.items.push({ ...payload })
          const { username } = payload
          const profile = new Profile({ ...payload, ...data })
          this._log('_loadList profile: ', profile)
          store.profiles.set(username, profile)
        }
        store.loaded = true
        this._log('_loadList initial load done')
      } catch (e) {
        this._log('_loadList initial load failed', e)
      } finally {
        store.loading = false
        this._initialLoadInFlight = false
      }
    }
  },
  watch: {
    user: {
      handler (value) {
        this._log('watch user', value)
        if (this.manageLifecycle) {
          if (value && this._hasRole()) {
            store.enabled = true
            this._loadList()
          } else {
            store.enabled = false
            store.selected = null
            store.items = []
          }
        }
      },
      immediate: true
    },
    selected: {
      handler (value) {
        this._log('watch selected: ', value)
        this.$nextTick(() => {
          this.$emit('change', this.scopes)
        })
      },
      immediate: true
    }
  },
  created () {
    this._initLogger('[Tickets][ViewAsProvider]')
    this._log('created log initialized')
  },
  mounted () {
    this._log('mounted', { manageLifecycle: this.manageLifecycle })
  },
  beforeDestroy () {
    this._log('beforeDestroy', { manageLifecycle: this.manageLifecycle })
  },
  render () {
    if (this.$scopedSlots.default !== undefined) {
      return this.$scopedSlots.default({
        state: {
          selected: this.selected,
          enabled: store.enabled,
          items: this.items,
          profile: this.profile,
          scopes: this.scopes,
          displayType: this.displayType
        },
        methods: {
          setItem: this.setItem,
          clearItem: this.clearItem
        }
      })
    }

    return this.$slots.default
  }
}

export default ViewAsProvider
